import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { computeMaintStatus, STATUS_COLORS, fmtNum, fmtDate } from "../lib/status";
import { Search, ChevronDown, ChevronUp, Archive, ArchiveRestore, AlertCircle, Plus, TruckIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export default function Fleet() {
  const { user } = useAuth();
  const [fleet, setFleet] = useState([]);
  const [intervals, setIntervals] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState({ type: null, division: null, status: null, dataCheck: false });
  const [showArchived, setShowArchived] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    const [f, i] = await Promise.all([
      api.get(`/fleet?include_archived=${showArchived}`),
      api.get("/intervals"),
    ]);
    setFleet(f.data); setIntervals(i.data);
  };
  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, [showArchived]);

  const types = useMemo(() => Array.from(new Set(fleet.map((u) => u.type).filter(Boolean))).sort(), [fleet]);
  const divisions = useMemo(() => Array.from(new Set(fleet.map((u) => u.division).filter(Boolean))).sort(), [fleet]);
  const statuses = useMemo(() => Array.from(new Set(fleet.map((u) => u.status).filter(Boolean))).sort(), [fleet]);

  const filtered = useMemo(() => {
    return fleet.filter((u) => {
      if (filter.type && u.type !== filter.type) return false;
      if (filter.division && u.division !== filter.division) return false;
      if (filter.status && u.status !== filter.status) return false;
      if (filter.dataCheck && !u.dataCheck) return false;
      if (q) {
        const hay = `${u.unit} ${u.desc} ${u.driver} ${u.vin} ${u.division}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [fleet, filter, q]);

  const isAdmin = user?.role === "admin";

  const onArchive = async (u) => {
    try {
      if (u.archived) await api.post(`/fleet/${u.id}/unarchive`);
      else await api.post(`/fleet/${u.id}/archive`);
      toast.success(u.archived ? "Restored" : "Archived");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="fleet-page">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Fleet</h1>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-1">
            {filtered.length} of {fleet.length} units
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-2 text-xs font-mono uppercase text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="show-archived-toggle"
              className="accent-red-500" />
            Show Archived
          </label>
          {isAdmin && (
            <button onClick={() => setAddOpen(true)} data-testid="add-unit-btn"
              className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-sm transition-colors flex items-center gap-1.5">
              <Plus className="h-3 w-3" /> Add Unit
            </button>
          )}
        </div>
      </div>

      {addOpen && <AddUnitDialog onClose={() => { setAddOpen(false); load(); }} types={types} divisions={divisions} statuses={statuses} />}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="fleet-search"
          placeholder="Search unit, VIN, driver, description…"
          className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 focus:ring-2 focus:ring-red-500/30 rounded-sm pl-9 pr-3 py-2 text-sm font-mono outline-none transition-colors"
        />
      </div>

      {/* Filter chip strips */}
      <FilterChips label="Type" options={types} value={filter.type} onChange={(v) => setFilter((f) => ({ ...f, type: v }))} idPrefix="chip-type" />
      <FilterChips label="Division" options={divisions} value={filter.division} onChange={(v) => setFilter((f) => ({ ...f, division: v }))} idPrefix="chip-div" />
      <FilterChips label="Status" options={statuses} value={filter.status} onChange={(v) => setFilter((f) => ({ ...f, status: v }))} idPrefix="chip-status" />
      <div>
        <button
          data-testid="chip-datacheck"
          onClick={() => setFilter((f) => ({ ...f, dataCheck: !f.dataCheck }))}
          className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
            filter.dataCheck ? "bg-red-500/20 border-red-500/60 text-red-200" : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:bg-zinc-800"
          }`}
        >
          <AlertCircle className="inline h-3 w-3 mr-1 -mt-0.5" /> Data Check Flags Only
        </button>
      </div>

      {/* Unit cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((u) => (
          <UnitCard key={u.id} unit={u} intervals={intervals}
            expanded={expanded === u.id}
            onToggle={() => setExpanded((e) => (e === u.id ? null : u.id))}
            onArchive={onArchive} isAdmin={isAdmin} refresh={load} user={user} />
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full border border-dashed border-zinc-800 rounded-sm p-8 text-center text-sm text-zinc-500">
            No units match your filters.
          </div>
        )}
      </div>
    </div>
  );
}

function FilterChips({ label, options, value, onChange, idPrefix }) {
  if (!options.length) return null;
  return (
    <div className="flex items-center gap-2">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 shrink-0 w-16">{label}</div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        <Chip active={value == null} onClick={() => onChange(null)} testId={`${idPrefix}-all`}>All</Chip>
        {options.map((o) => (
          <Chip key={o} active={value === o} onClick={() => onChange(o)} testId={`${idPrefix}-${slug(o)}`}>{o}</Chip>
        ))}
      </div>
    </div>
  );
}

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick} data-testid={testId}
      className={`shrink-0 text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
        active ? "bg-zinc-100 text-zinc-900 border-zinc-100" : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800"
      }`}
    >{children}</button>
  );
}

function slug(s) { return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-"); }

// Local (not UTC) YYYY-MM-DD so the date the user sees matches the date stored.
function localToday() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function AddUnitDialog({ onClose, types, divisions, statuses }) {
  const [form, setForm] = useState({
    unit: "", type: "", division: "", driver: "", desc: "",
    vin: "", fuelCard: "", mileage: "", hours: "",
    status: "Active", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.unit || !form.type) { toast.error("Unit and Type are required"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.mileage !== "") payload.mileage = Number(payload.mileage); else delete payload.mileage;
      if (payload.hours !== "") payload.hours = Number(payload.hours); else delete payload.hours;
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/fleet", payload);
      toast.success(`Added ${form.unit}`);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><TruckIcon className="h-4 w-4 text-red-400" /> Add New Unit</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <F label="Unit # / Name *" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} testId="add-unit-name" placeholder="e.g. 148 or SALT 001" />
            <F label="Type *" value={form.type} onChange={(v) => setForm({ ...form, type: v })} list={Array.from(new Set([...types, "Truck", "Trailer", "Heavy Equipment", "Vehicle (RV)", "Salt Spreader", "generators", "shop vacs"]))} testId="add-unit-type" placeholder="Truck, Salt Spreader…" />
          </div>
          <F label="Description" value={form.desc} onChange={(v) => setForm({ ...form, desc: v })} testId="add-unit-desc" placeholder="2025 Ford F-350 Salt Spreader" />
          <div className="grid grid-cols-2 gap-2">
            <F label="Division" value={form.division} onChange={(v) => setForm({ ...form, division: v })} list={divisions} testId="add-unit-division" />
            <F label="Driver" value={form.driver} onChange={(v) => setForm({ ...form, driver: v })} testId="add-unit-driver" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <F label="VIN" value={form.vin} onChange={(v) => setForm({ ...form, vin: v })} testId="add-unit-vin" />
            <F label="Fuel Card" value={form.fuelCard} onChange={(v) => setForm({ ...form, fuelCard: v })} testId="add-unit-fuelcard" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <F label="Mileage" type="number" value={form.mileage} onChange={(v) => setForm({ ...form, mileage: v })} testId="add-unit-mileage" />
            <F label="Hours" type="number" value={form.hours} onChange={(v) => setForm({ ...form, hours: v })} testId="add-unit-hours" />
          </div>
          <F label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} list={Array.from(new Set([...statuses, "Active", "Inactive", "SOLD"]))} testId="add-unit-status" />
          <F label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="add-unit-notes" />
        </div>
        <DialogFooter>
          <button onClick={onClose} className="text-xs font-mono uppercase px-3 py-1.5 border border-zinc-700 rounded-sm hover:bg-zinc-800 transition-colors">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="add-unit-submit"
            className="text-xs font-mono uppercase px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-sm transition-colors disabled:opacity-50">
            {busy ? "Saving…" : "Add Unit"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, value, onChange, type = "text", list, testId, placeholder }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} list={list ? `${testId}-list` : undefined} data-testid={testId} placeholder={placeholder}
        className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded-sm px-2 py-1.5 font-mono text-sm outline-none" />
      {list && <datalist id={`${testId}-list`}>{list.map((o) => <option key={o} value={o} />)}</datalist>}
    </div>
  );
}

function UnitCard({ unit, intervals, expanded, onToggle, onArchive, isAdmin, refresh, user }) {
  const status = computeMaintStatus(unit, intervals);
  const colors = STATUS_COLORS[status.key];
  const [editing, setEditing] = useState(false);
  const [mile, setMile] = useState(unit.mileage ?? "");
  const [hours, setHours] = useState(unit.hours ?? "");
  const [editingNotes, setEditingNotes] = useState(false);
  const [noteDraft, setNoteDraft] = useState(unit.notes || "");

  useEffect(() => { setMile(unit.mileage ?? ""); setHours(unit.hours ?? ""); }, [unit.mileage, unit.hours]);
  useEffect(() => { setNoteDraft(unit.notes || ""); }, [unit.notes]);

  const saveNotes = async () => {
    try {
      await api.patch(`/fleet/${unit.id}`, { notes: noteDraft });
      toast.success("Notes saved");
      setEditingNotes(false);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveMileage = async () => {
    try {
      const payload = {};
      if (mile !== "" && Number(mile) !== unit.mileage) payload.mileage = Number(mile);
      if (hours !== "" && Number(hours) !== unit.hours) payload.hours = Number(hours);
      if (!Object.keys(payload).length) return setEditing(false);
      await api.patch(`/fleet/${unit.id}`, payload);
      toast.success("Updated");
      setEditing(false);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className={`border rounded-sm bg-zinc-900 transition-colors ${expanded ? "border-red-500/60" : "border-zinc-800 hover:border-zinc-700"}`} data-testid={`unit-card-${unit.id}`}>
      <button onClick={onToggle} data-testid={`unit-toggle-${unit.id}`} className="w-full text-left p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-zinc-50">{unit.unit}</span>
              {unit.archived && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 border border-zinc-700 text-zinc-500">ARCHIVED</span>}
            </div>
            <div className="text-sm text-zinc-300 mt-0.5 truncate">{unit.desc || "—"}</div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] font-mono text-zinc-500">
              <span>{unit.type}</span>
              {unit.division && <span>· {unit.division}</span>}
              {unit.driver && <span>· {unit.driver}</span>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <span className={`inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${colors.badge}`}>{status.label}</span>
            <div className="mt-2 font-mono text-sm text-zinc-300">{fmtNum(unit.mileage)}<span className="text-zinc-500 text-xs"> mi</span></div>
          </div>
        </div>
        {unit.dataCheck && (
          <div className="mt-2 flex items-center gap-1.5 text-[11px] font-mono text-amber-400">
            <AlertCircle className="h-3 w-3" /> {unit.dataCheck}
          </div>
        )}
        <div className="mt-3 text-zinc-600 flex items-center justify-center">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-zinc-800 p-4 space-y-4">
          <Link to={`/fleet/${unit.id}`} data-testid={`unit-open-detail-${unit.id}`}
            className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-red-400 hover:text-red-300 transition-colors">
            Open Full Detail Page →
          </Link>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[11px]">
            <DataRow label="VIN" value={unit.vin} />
            {isAdmin && <DataRow label="VIN Admin" value={unit.vinAdmin} />}
            <DataRow label="Fuel Card" value={unit.fuelCard} />
            <DataRow label="Status" value={unit.status} />
            <DataRow label="DOT Date" value={fmtDate(unit.dotDate)} />
            <DataRow label="Tag Exp" value={unit.tagExp} />
            <DataRow label="Last Service" value={unit.lastServiceDate ? fmtDate(unit.lastServiceDate) : (fmtNum(unit.lastServiceMileage) + " mi")} />
            <DataRow label="Next Annual" value={fmtDate(unit.nextAnnualInspection)} />
          </div>

          {/* Notes */}
          <div className="border-l-2 border-red-500/40 pl-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-3">
              <span>Notes</span>
              {isAdmin && !editingNotes && (
                <button onClick={() => setEditingNotes(true)} data-testid={`unit-notes-edit-${unit.id}`}
                  className="text-red-400 hover:text-red-300 transition-colors">
                  {unit.notes ? "Edit" : "+ Add"}
                </button>
              )}
            </div>
            {editingNotes ? (
              <div className="space-y-2">
                <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} rows={3}
                  data-testid={`unit-notes-textarea-${unit.id}`}
                  placeholder="Condition, quirks, mods, service history you want visible…"
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500 rounded-sm p-2 text-sm font-mono outline-none resize-y" />
                <div className="flex gap-2">
                  <button onClick={saveNotes} data-testid={`unit-notes-save-${unit.id}`}
                    className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-sm transition-colors">
                    Save Notes
                  </button>
                  <button onClick={() => { setEditingNotes(false); setNoteDraft(unit.notes || ""); }}
                    className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 border border-zinc-700 hover:bg-zinc-800 rounded-sm transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              unit.notes
                ? <p className="text-xs text-zinc-300 italic whitespace-pre-wrap">{unit.notes}</p>
                : <p className="text-xs text-zinc-600 italic">{isAdmin ? "No notes yet — click + Add to write one." : "No notes yet."}</p>
            )}
          </div>

          {isAdmin && (<>
          {/* Quick update */}
          <div className="border-t border-zinc-800 pt-3">
            {!editing ? (
              <button onClick={() => setEditing(true)} data-testid={`unit-edit-${unit.id}`}
                className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 border border-zinc-700 rounded-sm hover:bg-zinc-800 transition-colors">
                Update Mileage / Hours
              </button>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Mileage</label>
                    <input type="number" value={mile} onChange={(e) => setMile(e.target.value)} data-testid={`unit-mileage-input-${unit.id}`}
                      className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500 rounded-sm px-2 py-1.5 font-mono text-sm outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Hours</label>
                    <input type="number" value={hours} onChange={(e) => setHours(e.target.value)} data-testid={`unit-hours-input-${unit.id}`}
                      className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500 rounded-sm px-2 py-1.5 font-mono text-sm outline-none" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={saveMileage} data-testid={`unit-save-${unit.id}`}
                    className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-sm transition-colors">Save</button>
                  <button onClick={() => setEditing(false)}
                    className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 border border-zinc-700 hover:bg-zinc-800 rounded-sm transition-colors">Cancel</button>
                </div>
              </div>
            )}
          </div>

          <ServiceLogSection unit={unit} refresh={refresh} />
          </>)}

          {isAdmin && (
            <div className="flex justify-end border-t border-zinc-800 pt-3">
              <button onClick={() => onArchive(unit)} data-testid={`unit-archive-${unit.id}`}
                className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 border border-zinc-700 hover:bg-zinc-800 rounded-sm transition-colors flex items-center gap-1.5">
                {unit.archived ? <><ArchiveRestore className="h-3 w-3" /> Restore</> : <><Archive className="h-3 w-3" /> Archive</>}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DataRow({ label, value }) {
  return (
    <>
      <div className="font-mono uppercase tracking-widest text-[9px] text-zinc-500 self-center">{label}</div>
      <div className="font-mono text-zinc-200 text-xs break-all">{value || "—"}</div>
    </>
  );
}

function ServiceLogSection({ unit, refresh }) {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({ kind: "service", mileage: "", hours: "", notes: "", date: localToday(), next_annual: "", parts_cost: "", labor_cost: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get(`/fleet/${unit.id}/service-logs`).then((r) => setLogs(r.data));
  }, [open, unit.id]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { kind: form.kind, notes: form.notes || null, date: form.date };
      if (form.mileage !== "") payload.mileage = Number(form.mileage);
      if (form.hours !== "") payload.hours = Number(form.hours);
      if (form.next_annual) payload.next_annual = form.next_annual;
      if (form.parts_cost !== "") payload.parts_cost = Number(form.parts_cost);
      if (form.labor_cost !== "") payload.labor_cost = Number(form.labor_cost);
      await api.post(`/fleet/${unit.id}/service`, payload);
      toast.success("Service logged");
      setForm({ ...form, mileage: "", hours: "", notes: "", parts_cost: "", labor_cost: "" });
      const r = await api.get(`/fleet/${unit.id}/service-logs`);
      setLogs(r.data);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-t border-zinc-800 pt-3">
      <button onClick={() => setOpen((o) => !o)} data-testid={`unit-service-toggle-${unit.id}`}
        className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 border border-zinc-700 rounded-sm hover:bg-zinc-800 transition-colors">
        {open ? "Hide Service Log" : "Log Service / Inspection"}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid={`service-kind-${unit.id}`}
              className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 font-mono text-xs outline-none">
              <option value="service">Service</option>
              <option value="annual_inspection">Annual Insp.</option>
            </select>
            <input placeholder="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 font-mono text-xs outline-none" />
            {form.kind === "service" ? (
              <>
                <input placeholder="Mileage" type="number" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} data-testid={`service-mileage-${unit.id}`}
                  className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 font-mono text-xs outline-none" />
                <input placeholder="Hours" type="number" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })}
                  className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 font-mono text-xs outline-none" />
              </>
            ) : (
              <input placeholder="Next annual date" type="date" value={form.next_annual} onChange={(e) => setForm({ ...form, next_annual: e.target.value })} data-testid={`service-next-annual-${unit.id}`}
                className="col-span-2 bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 font-mono text-xs outline-none" />
            )}
          </div>
          <input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 font-mono text-xs outline-none" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Parts Cost ($)</label>
              <input type="number" step="0.01" min="0" placeholder="0.00" value={form.parts_cost}
                onChange={(e) => setForm({ ...form, parts_cost: e.target.value })}
                data-testid={`service-parts-cost-${unit.id}`}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500 rounded-sm px-2 py-1.5 font-mono text-xs outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Labor Cost ($)</label>
              <input type="number" step="0.01" min="0" placeholder="0.00" value={form.labor_cost}
                onChange={(e) => setForm({ ...form, labor_cost: e.target.value })}
                data-testid={`service-labor-cost-${unit.id}`}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-500 rounded-sm px-2 py-1.5 font-mono text-xs outline-none" />
            </div>
          </div>
          <button onClick={submit} disabled={busy} data-testid={`service-submit-${unit.id}`}
            className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-red-600 hover:bg-red-500 disabled:opacity-50 rounded-sm transition-colors">
            {busy ? "Saving…" : "Log Service"}
          </button>

          <ul className="divide-y divide-zinc-800 border border-zinc-800 rounded-sm">
            {logs.length === 0 && <li className="p-3 text-xs text-zinc-500">No previous service logs.</li>}
            {logs.map((l) => (
              <li key={l.id} className="p-2.5 text-xs flex items-center justify-between">
                <div>
                  <div className="font-mono uppercase text-[10px] tracking-wider text-zinc-500">{l.kind}</div>
                  <div className="text-zinc-200">{fmtDate(l.date)} · {l.username}</div>
                  {(l.mileage != null || l.hours != null) && (
                    <div className="text-zinc-500 font-mono text-[10px]">
                      {l.mileage != null && <>{fmtNum(l.mileage)} mi </>}
                      {l.hours != null && <>{fmtNum(l.hours)} hr</>}
                    </div>
                  )}
                  {(l.parts_cost != null || l.labor_cost != null) && (
                    <div className="text-zinc-500 font-mono text-[10px] mt-0.5 flex gap-3 flex-wrap">
                      {l.parts_cost != null && <span>Parts <span className="text-zinc-200">${Number(l.parts_cost).toFixed(2)}</span></span>}
                      {l.labor_cost != null && <span>Labor <span className="text-zinc-200">${Number(l.labor_cost).toFixed(2)}</span></span>}
                      {(l.total_cost != null || l.parts_cost != null || l.labor_cost != null) && (
                        <span className="text-red-300">Total ${Number(l.total_cost ?? ((l.parts_cost || 0) + (l.labor_cost || 0))).toFixed(2)}</span>
                      )}
                    </div>
                  )}
                  {l.notes && <div className="text-zinc-400 italic mt-0.5">{l.notes}</div>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
